import { useState, useEffect } from 'react';
import {
    Grid2 as Grid,
    Typography,
    TextField,
    Button,
    Paper,
    Box,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert
} from '@mui/material';
import { SelectionButton } from '../../../component/buttons';
import { useClinicSettings } from '../../../hooks/useClinicSettings';
import { MainCard } from 'component/cards/MainCard2';

const RESULT_STRUCTURE = {
    effort: [
        {
            title: 'Sub-secção 1',
            fields: ['PAS repouso', 'PAS esforço']
        },
        {
            title: 'Sub-secção 2',
            fields: ['Repouso', 'Pico FC', 'FCMPI', '% FC prevista']
        },
        {
            title: 'Sub-secção 3',
            fields: ['Extrassístoles SV', 'Extrassístoles V', 'Angina', 'Alt. Seg. ST']
        },
        {
            title: 'Sub-secção 4',
            fields: ['METS', '1º min Recup']
        }
    ],

    cardiorespiratory: [
        {
            title: 'PE Clássica',
            fields: [
                'Suspensão por',
                'PA repouso',
                'PA esforço',
                'Extrassístoles V',
                'Angina',
                'Alt. Seg. ST',
                'METS'
            ]
        },
        {
            title: 'PERC',
            fields: [
                'Pico',
                '% previsto',
                'VT1%picoVO2',
                'Quociente Resp.',
                'O2 pulso % prev.'
            ]
        },
        {
            title: 'Ventilação',
            fields: [
                'FEV1/FVC',
                'Res. Respiratória',
                'Osc. Ventilatória',
                'VE/VCO2',
                'EqCO2 VT1',
                'PETCO2 VT1',
                'ΔPETCO2'
            ]
        },
        {
            title: 'Cronotropia e Treino',
            fields: ['% FC prevista', '1º min recup', '3º min recup']
        }
    ]
};

const StressTestRangeConfig = () => {
    // The current backend permits DOCTOR to read clinic settings, but not edit them.
    const canEdit = false;
    const { parameters, saveSettings, isLoading, isSaving, isSaveSuccess, isSaveError } =
        useClinicSettings();

    const [ranges, setRanges] = useState({});
    const [tab, setTab] = useState('effort');

    const [feedbackOpen, setFeedbackOpen] = useState(false);
    const [feedbackType, setFeedbackType] = useState(null);

    useEffect(() => {
        if (parameters) {
            setRanges(parameters);
        }
    }, [parameters]);

    useEffect(() => {
        if (isSaveSuccess) {
            setFeedbackType('success');
            setFeedbackOpen(true);
        }

        if (isSaveError) {
            setFeedbackType('error');
            setFeedbackOpen(true);
        }
    }, [isSaveSuccess, isSaveError]);

    const handleChange = (field, level, key, value) => {
        setRanges((prev) => ({
            ...prev,
            [field]: {
                ...prev[field],
                [level]: {
                    ...prev[field][level],
                    [key]: Number(value)
                }
            }
        }));
    };

    const handleSave = () => {
        if (!canEdit) return;
        saveSettings({
            parameters: ranges,
            reason: 'Atualização de ranges clínicos'
        });
    };

    const renderField = (field) => {
        const levels = ranges[field];
        if (!levels) return null;

        return (
            <Paper key={field} sx={{ p: 1, mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} color="#1976d2">
                    {field}
                </Typography>

                {Object.entries(levels).map(([level, range]) => (
                    <Grid
                        container
                        spacing={1}
                        alignItems="center"
                        key={level}
                        sx={{ mt: 1 }}
                    >
                        <Grid size={2}>
                            <Typography>{level}</Typography>
                        </Grid>
                        <Grid size={2}>
                            <TextField
                                label="Min"
                                type="number"
                                size="small"
                                disabled={!canEdit}
                                value={range.min}
                                onChange={(e) =>
                                    handleChange(field, level, 'min', e.target.value)
                                }
                            />
                        </Grid>
                        <Grid size={2}>
                            <TextField
                                label="Max"
                                type="number"
                                size="small"
                                disabled={!canEdit}
                                value={range.max}
                                onChange={(e) =>
                                    handleChange(field, level, 'max', e.target.value)
                                }
                            />
                        </Grid>
                    </Grid>
                ))}
            </Paper>
        );
    };

    const renderTabContent = () => {
        if (!ranges) return null;
        return RESULT_STRUCTURE[tab].map((section) => (
            <Box key={section.title} sx={{ mb: 4 }}>
                <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{ mb: 2, borderBottom: '1px solid #ddd', pb: 1 }}
                >
                    {section.title}
                </Typography>

                <Grid container spacing={3}>
                    {section.fields
                        .filter((field) => ranges[field])
                        .map((field) => (
                            <Grid key={field} size={{ xs: 12, md: 4 }}>
                                {renderField(field)}
                            </Grid>
                        ))}
                </Grid>
            </Box>
        ));
    };

    if (isLoading || !ranges) {
        return (
            <MainCard title="Configuração de Ranges Clínicos">
                <Box display="flex" justifyContent="center" py={6}>
                    <CircularProgress />
                </Box>
            </MainCard>
        );
    }

    return (
        <MainCard title="Configuração de Ranges Clínicos">
            {!canEdit && (
                <Alert severity="info" sx={{ mb: 3 }}>
                    Estes valores são comuns à clínica e estão disponíveis para consulta
                    neste perfil.
                </Alert>
            )}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 6 }}>
                    <SelectionButton
                        selected={tab === 'effort'}
                        sx={{ width: '100%', py: 2 }}
                        onClick={() => setTab('effort')}
                    >
                        Prova de Esforço
                    </SelectionButton>
                </Grid>

                <Grid size={{ xs: 6 }}>
                    <SelectionButton
                        selected={tab === 'cardiorespiratory'}
                        sx={{ width: '100%', py: 2 }}
                        onClick={() => setTab('cardiorespiratory')}
                    >
                        Prova de Esforço Cardiorrespiratória
                    </SelectionButton>
                </Grid>
            </Grid>

            {renderTabContent()}

            {canEdit && (
                <Box textAlign="right">
                    <Button variant="contained" onClick={handleSave} disabled={isSaving}>
                        {isSaving ? 'A guardar…' : 'Guardar Ranges'}
                    </Button>
                </Box>
            )}
            <Dialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)}>
                <DialogTitle>
                    {feedbackType === 'success' ? 'Sucesso' : 'Erro'}
                </DialogTitle>

                <DialogContent>
                    {feedbackType === 'success' && (
                        <Alert severity="success">
                            Os ranges clínicos foram guardados com sucesso.
                        </Alert>
                    )}

                    {feedbackType === 'error' && (
                        <Alert severity="error">
                            Ocorreu um erro ao guardar os ranges clínicos. Tente
                            novamente.
                        </Alert>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setFeedbackOpen(false)} autoFocus>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </MainCard>
    );
};

export default StressTestRangeConfig;
